import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search, User, Shield, ShieldCheck, ShieldAlert, RefreshCw, Loader2 } from "lucide-react";

const ROLE_TONE = {
  admin: { icon: ShieldCheck, bg: "bg-zinc-900 text-white" },
  driver: { icon: ShieldAlert, bg: "bg-[#FFC60A]" },
  parent: { icon: User, bg: "bg-zinc-200" },
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    total: users.length,
    admin: users.filter((u) => u.role === "admin").length,
    driver: users.filter((u) => u.role === "driver").length,
    parent: users.filter((u) => u.role === "parent").length,
  }), [users]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!q) return true;
      return (u.name + " " + (u.email || "") + " " + (u.phone || "")).toLowerCase().includes(q);
    });
  }, [users, search, roleFilter]);

  return (
    <div className="p-6 lg:p-10" data-testid="admin-users-page">
      <header className="mb-6 flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Comptes & rôles</div>
          <h1 className="font-display font-black uppercase text-4xl lg:text-5xl tracking-tighter">Utilisateurs</h1>
          <p className="text-sm text-zinc-600 mt-1">
            {counts.total} comptes · {counts.admin} admin · {counts.driver} chauffeurs · {counts.parent} parents
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="refresh-btn"
          className="inline-flex items-center gap-2 bg-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#ffd43a]">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rafraîchir
        </button>
      </header>

      <div className="max-w-5xl border border-zinc-900">
        <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white flex items-center gap-2 flex-wrap">
          <Search size={12} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrer…"
            data-testid="search-input" className="flex-1 min-w-[160px] bg-transparent outline-none text-xs font-mono text-white placeholder:text-zinc-500" />
          <div className="flex gap-1">
            {["all", "admin", "driver", "parent"].map((r) => (
              <button key={r} onClick={() => setRoleFilter(r)} data-testid={`filter-${r}`}
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-white/30 ${roleFilter === r ? "bg-[#FFC60A] text-zinc-900 border-[#FFC60A]" : "text-zinc-300 hover:bg-zinc-800"}`}>
                {r === "all" ? "Tous" : r}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-zinc-200">
          {filtered.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 text-center">Aucun utilisateur correspondant.</div>
          ) : filtered.map((u) => {
            const role = ROLE_TONE[u.role] || ROLE_TONE.parent;
            const RoleIcon = role.icon;
            return (
              <div key={u.id} data-testid={`user-${u.id}`} className="p-3 flex items-center gap-3 hover:bg-zinc-50">
                <div className="w-9 h-9 border border-zinc-900 bg-zinc-50 flex items-center justify-center shrink-0">
                  <User size={15} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-sm tracking-tight truncate">{u.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${role.bg}`}>
                  <RoleIcon size={11} /> {u.role}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[10px] font-mono text-zinc-500 mt-3">
        Création d'utilisateurs supplémentaires : utilisez le formulaire d'inscription public <code className="bg-zinc-100 px-1">/register</code>.
      </p>
    </div>
  );
}
